import { useEffect, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { getMyAccess, SelfServiceAgent, ApiError } from '../lib/api-client';
import { ConnectPanel } from '../components/ConnectPanel';
import { config } from '../config/env';

// Pre-consent only applies to STS-brokered connections — mirrors the admin
// UI's per-resource pre-consent button (CopyPreConsentResourceButton).
const buildPreConsentUrl = (agentId: string, resourceName: string) =>
  `${config.gatewayUrl.replace(/\/$/, '')}/oauth/pre-consent?agent_id=${encodeURIComponent(
    agentId
  )}&resource=${encodeURIComponent(resourceName)}`;

// Agent-level pre-consent — same endpoint, no `resource` param, checks
// every STS-brokered connection under the agent in one pass.
const buildAgentPreConsentUrl = (agentId: string) =>
  `${config.gatewayUrl.replace(/\/$/, '')}/oauth/pre-consent?agent_id=${encodeURIComponent(agentId)}`;

// Sentinel used as the "resource" half of the expandedResource key when the
// agent-level (not per-resource) Connect flyout is open.
const AGENT_CONNECT_KEY = '__agent__';

export const MyAgents = () => {
  const [agents, setAgents] = useState<SelfServiceAgent[] | null>(null);
  const [agentRoutingEnabled, setAgentRoutingEnabled] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedResource, setExpandedResource] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    getMyAccess()
      .then((response) => {
        if (!cancelled) {
          setAgents(response.agents);
          setAgentRoutingEnabled(response.agent_routing_enabled);
        }
      })
      .catch((err: ApiError) => {
        if (!cancelled) {
          setError(err.message || 'Failed to load your access');
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div>
      <h1 className="page-title">My Agents</h1>
      {error ? (
        <p className="error">{error}</p>
      ) : agents === null ? (
        <div className="centered">
          <div className="spinner" />
          <p className="muted">Loading your access...</p>
        </div>
      ) : agents.length === 0 ? (
        <p className="muted">
          You don't have access to any agents yet. Ask an admin to assign you to
          one in Okta.
        </p>
      ) : (
        <ul className="agent-list">
          {agents.map((agent) => {
            const agentConnectKey = `${agent.agent_id}:${AGENT_CONNECT_KEY}`;
            const isAgentConnectExpanded = expandedResource === agentConnectKey;
            const agentHasStsResource = agent.resources.some(
              (r) => r.connection_type === 'STS_ACCESS_TOKEN'
            );
            return (
            <li key={agent.agent_id} className="agent-card">
              <div className="owner-row">
                <div>
                  <span className="agent-card-label">Agent</span>
                  <h2 className="agent-card-title">{agent.agent_name}</h2>
                  <div className="agent-actions">
                    {agentHasStsResource ? (
                      <a
                        className="agent-action"
                        href={buildAgentPreConsentUrl(agent.agent_id)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Pre-Consent
                      </a>
                    ) : null}
                    {agentRoutingEnabled ? (
                      <button
                        type="button"
                        className="agent-action"
                        onClick={() =>
                          setExpandedResource(isAgentConnectExpanded ? null : agentConnectKey)
                        }
                        aria-expanded={isAgentConnectExpanded}
                      >
                        Connect
                        <ChevronRight size={12} />
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
              {isAgentConnectExpanded ? (
                <ConnectPanel agentName={agent.agent_name} agentRoutingEnabled={agentRoutingEnabled} />
              ) : null}
              {agent.resources.length === 0 ? (
                <p className="muted" style={{ marginTop: '0.75rem' }}>
                  No resources configured for this agent.
                </p>
              ) : (
                <ul className="resource-list" style={{ marginTop: '0.75rem' }}>
                  {agent.resources.map((resource) => {
                    const resourceKey = `${agent.agent_id}:${resource.name}`;
                    const isExpanded = expandedResource === resourceKey;
                    const info = (
                      <span className="resource-info">
                        <span className="resource-name">{resource.name}</span>
                        {resource.description ? (
                          <span className="resource-description">{resource.description}</span>
                        ) : null}
                      </span>
                    );
                    const showPreConsent = resource.connection_type === 'STS_ACCESS_TOKEN';
                    return (
                      <li key={resource.name} className="resource-item">
                        <div className="resource-row">
                          {info}
                          <div className="owner-actions">
                            {showPreConsent ? (
                              <a
                                className="resource-action"
                                href={buildPreConsentUrl(agent.agent_id, resource.name)}
                                target="_blank"
                                rel="noreferrer"
                              >
                                Pre-Consent
                              </a>
                            ) : null}
                            <button
                              type="button"
                              className="resource-action"
                              onClick={() => setExpandedResource(isExpanded ? null : resourceKey)}
                              aria-expanded={isExpanded}
                            >
                              Connect
                              <ChevronRight size={14} />
                            </button>
                          </div>
                        </div>
                        {isExpanded ? (
                          <ConnectPanel
                            agentName={agent.agent_name}
                            resourceName={resource.name}
                            agentRoutingEnabled={agentRoutingEnabled}
                          />
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
